import React from "react";
import AgentLoopDiagram from "./diagrams/AgentLoopDiagram";
import StacksAndSubagentsDiagram from "./diagrams/StacksAndSubagentsDiagram";

function Agent101Content() {

  return (
    <div>
      <h2>Agent 101</h2>
      <p>
        To understand the power of agentic workflows, it helps to view them as
        layers of capability — starting with raw intelligence and ending with
        orchestrated action.
      </p>

      <h4>1. The Brain: Large Language Models (LLMs)</h4>
      <p>
        LLMs are the reasoning engines of modern AI. Trained on vast amounts
        of text data, they can understand instructions, plan complex tasks,
        and generate human-like text. However, on their own, they are
        passive — they can answer questions, but they cannot "do" things
        outside of a chat window.
      </p>

      <h4>2. The Eyes: Vision Language Models (VLMs)</h4>
      <p>
        VLMs extend AI capabilities into the visual world. By processing text
        and images simultaneously, they allow the system to "see" charts,
        verify UI elements, or analyze photos — providing crucial context
        that text alone cannot capture. Stackform supports image uploads
        (PNG, JPG, WEBP) that agents can analyze and describe.
      </p>

      <h4>3. The Body: AI Agents</h4>
      <p>
        If an LLM is the brain, an <strong>AI Agent</strong> is the brain
        given hands (tools) and a mandate to act.
      </p>
      <p>
        An agent wraps an LLM in a control loop: it <strong>perceives</strong> a
        task, <strong>reasons</strong> about the steps needed, <strong>uses
        tools</strong> (like web browsers, APIs, or search engines) to execute
        those steps, and <strong>observes</strong> the results to self-correct.
        It shifts the focus from "generating text" to "completing jobs."
      </p>

      <AgentLoopDiagram />

      <h4>4. The Team: Specialist Stacks</h4>
      <p>
        Rarely does one employee do everything in a company; the same applies
        to AI. <strong>Agentic Workflows</strong> occur when multiple specialized
        agents collaborate to solve complex problems. Instead of one generalist
        trying to do it all, an <strong>Orchestrator</strong> breaks a large
        goal into sub-tasks and routes them to specialists.
      </p>
      <p>
        Stackform gives you this pre-built team out of the box as a set of{" "}
        <strong>stacks</strong> — each a focused agentic service:
      </p>
      <ul>
        <li><strong>Orchestrator</strong> — plans and coordinates the work.</li>
        <li><strong>Generalist</strong> — real-time web &amp; news search.</li>
        <li><strong>Researcher</strong> — digs into academic literature.</li>
        <li><strong>Evaluator</strong> — audits the quality of an answer.</li>
        <li>
          <strong>Challenger</strong> — stress-tests claims and surfaces
          counter-arguments.
        </li>
      </ul>

      <h4>5. Inside Each Stack: Aeri, Capo &amp; Cardian</h4>
      <p>
        Each stack runs a small pool of three <strong>subagents</strong> —{" "}
        <strong>Aeri</strong>, <strong>Capo</strong>, and{" "}
        <strong>Cardian</strong> — that do the actual model calls. By default
        they use three different model families (Aeri → GPT, Capo → Gemini,
        Cardian → Claude), so every query can get a blend of perspectives — but
        you're in full control: assign any provider, model, and persona to each
        under <strong>Settings → Model Defaults → Agent Models</strong>, or set
        an agent's provider to <strong>None</strong> to disable it and run on
        just one or two. The assignment applies across every stack, and you can
        add your own custom agents too.
      </p>

      <StacksAndSubagentsDiagram />

      <p>
        In <strong>Workflows</strong>, you chain these stacks together into
        multi-step pipelines — for example, Generalist → Researcher → Evaluator
        — with each step passing its output to the next.
      </p>
    </div>
  );
}

export default Agent101Content;